import { BackendServiceUrl, BaseService } from './BaseService';
import { UpdateUserMemoryOptInRequest, UserMemory, isUserMemory } from '../models/UserMemory';

// getResponseAsync returns a shapeless `{}` on a 429 or empty-body 2xx (see BaseService), typed as
// UserMemory but not actually one. Reject it so the hook's revert/error path fires instead of a
// note-less object reaching the store and crashing the settings section's `notes.length` read.
const parseMemory = (value: unknown): UserMemory => {
    if (!isUserMemory(value)) {
        throw new Error('Received a malformed or rate-limited memory response.');
    }
    return value;
};

// The DELETE endpoints return 204 with no body, so getResponseWithHeadersAsync reads the status
// directly: unlike getResponseAsync it does not throw on a non-2xx, and a rate-limited (429) delete
// would otherwise be indistinguishable from a 204 success — the optimistic removal would stick until
// a later refetch resurrected the note. Reject any non-2xx so the hook's revert fires.
const ensureSuccessStatus = (status: number): void => {
    if (status < 200 || status >= 300) {
        throw new Error(`Memory request failed with status ${status.toString()}.`);
    }
};

export class MemoryService extends BaseService {
    constructor(accessToken: string, baseServiceUrl: string = BackendServiceUrl) {
        super(accessToken, baseServiceUrl);
    }

    public async getMemoryAsync(): Promise<UserMemory> {
        const memory: unknown = await this.getResponseAsync<UserMemory>({
            commandPath: 'memory/me',
            method: 'GET',
        });
        return parseMemory(memory);
    }

    public async updateOptInAsync(optedIn: boolean): Promise<UserMemory> {
        const request: UpdateUserMemoryOptInRequest = { optedIn };
        const memory: unknown = await this.getResponseAsync<UserMemory>({
            commandPath: 'memory/me/opt-in',
            method: 'PUT',
            body: request,
        });
        return parseMemory(memory);
    }

    public async deleteNoteAsync(id: string): Promise<void> {
        // The id is a server-validated slug, but it still forms a path segment — encode it so an
        // unexpected character can't alter the route.
        const { status } = await this.getResponseWithHeadersAsync({
            commandPath: `memory/me/${encodeURIComponent(id)}`,
            method: 'DELETE',
        });
        ensureSuccessStatus(status);
    }

    public async deleteAllAsync(): Promise<void> {
        const { status } = await this.getResponseWithHeadersAsync({
            commandPath: 'memory/me',
            method: 'DELETE',
        });
        ensureSuccessStatus(status);
    }
}
